import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import useFormValidation from '../hooks/useFormValidation';
import { validators } from '../utils/validation';
import api from '../services/api';
import ResetPasswordModal from '../components/auth/ResetPasswordModal';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loading, isAuthenticated } = useAuth();
  const { refreshLocations, setSelectedLocation } = useLocation();
  const [showResetModal, setShowResetModal] = useState(false);

  // Pobierz URL do przekierowania po zalogowaniu
  const redirectTo = searchParams.get('redirect') || '/pos';
  // QR token z URL (telefon po zeskanowaniu QR z kolektora)
  const qrTokenParam = searchParams.get('qr') || '';

  const [credentials, setCredentials] = useState({
    login: '',
    password: ''
  });
  const [error, setError] = useState(() => {
    const reason = searchParams.get('reason');
    if (reason === 'device_blocked') return 'Urządzenie zostało zablokowane przez administratora. Wylogowano.';
    if (reason === 'session_expired') return 'Sesja czasowa wygasła. Zaloguj się ponownie.';
    return null;
  });

  // QR token validation state (telefon — po zeskanowaniu QR z kolektora)
  const [qrValid, setQrValid] = useState(null); // null = ładowanie, true/false
  const [qrSessionMinutes, setQrSessionMinutes] = useState(0);

  const validationRules = useMemo(() => ({
    login: [(v) => validators.required(v, 'Login')],
    password: [(v) => validators.required(v, 'Hasło')],
  }), []);

  const { errors: fieldErrors, validate, clearError } = useFormValidation(validationRules);

  // Jeśli już zalogowany, przekieruj na docelową stronę
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, navigate, redirectTo]);

  // === TELEFON: Waliduj QR token z URL (po zeskanowaniu QR z kolektora) ===
  useEffect(() => {
    if (qrTokenParam && /^[a-f0-9]{64}$/.test(qrTokenParam)) {
      setQrValid(null); // loading
      api.get(`/auth/qr-token/${qrTokenParam}/validate`)
        .then(res => {
          const data = res.data?.data;
          if (data?.valid) {
            setQrValid(true);
            setQrSessionMinutes(data.session_duration_minutes || 240);
          } else {
            setQrValid(false);
            setError(
              data?.reason === 'expired' ? 'Kod QR wygasł. Wygeneruj nowy w kolektorze.' :
              data?.reason === 'already_used' ? 'Ten kod QR został już użyty. Wygeneruj nowy.' :
              'Nieprawidłowy kod QR.'
            );
          }
        })
        .catch(() => {
          setQrValid(false);
          setError('Nie udało się zweryfikować kodu QR.');
        });
    } else if (qrTokenParam) {
      setQrValid(false);
      setError('Nieprawidłowy format kodu QR.');
    }
  }, [qrTokenParam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validate(credentials)) {
      return;
    }

    try {
      // Dodaj qr_token do payloadu jeśli obecny i ważny
      const loginData = { ...credentials };
      if (qrTokenParam && qrValid) {
        loginData.qr_token = qrTokenParam;
      }

      const result = await login(loginData);

      if (result.success) {
        // Jeśli serwer zwrócił device_expires_at (sesja QR), zapisz do sessionStorage
        if (result.user?.device_expires_at) {
          sessionStorage.setItem('v3sys_device_expires_at', result.user.device_expires_at);
        }
        setSelectedLocation(null);
        localStorage.removeItem('selectedLocation');
        // Pełne przeładowanie strony - to resetuje wszystkie konteksty i ładuje uprawnienia od nowa
        window.location.href = redirectTo;
        return;
      } else {
        // Trusted Devices — dedykowane komunikaty
        const code = result.errorCode;
        if (code === 'DEVICE_NOT_TRUSTED' || code === 'DEVICE_PENDING') {
          setError('To urządzenie nie zostało jeszcze zatwierdzone przez administratora. Poczekaj na zatwierdzenie i spróbuj ponownie.');
        } else if (code === 'DEVICE_BLOCKED') {
          setError('To urządzenie zostało zablokowane. Skontaktuj się z administratorem systemu.');
        } else {
          setError(result.error || 'Nieprawidłowy login lub hasło');
        }
      }
    } catch (err) {
      console.error('Błąd logowania:', err);
      setError('Błąd połączenia z serwerem');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    clearError(name);
  };

  const sessionHours = Math.round(qrSessionMinutes / 60 * 10) / 10;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, var(--purple) 0%, var(--purple) 100%)',
        padding: '20px'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          background: 'var(--card-bg)',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden'
        }}
      >
        {/* Header z logo */}
        <div
          style={{
            background: 'linear-gradient(135deg, var(--success), var(--success))',
            padding: '30px 20px',
            textAlign: 'center',
            color: 'white'
          }}
        >
          <div
            style={{
              width: '70px',
              height: '70px',
              background: 'var(--bg-secondary)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 15px',
              fontSize: '32px'
            }}
          >
            📊
          </div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700 }}>V3SYS</h1>
          <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: '14px' }}>System zarządzania</p>
        </div>

        {/* Formularz */}
        <div style={{ padding: '30px' }}>
          {/* Banner QR — telefon po zeskanowaniu (valid) */}
          {qrTokenParam && qrValid && (
            <div
              style={{
                background: '#d4edda',
                color: '#155724',
                padding: '12px 15px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px',
                border: '1px solid #c3e6cb',
                textAlign: 'center'
              }}
            >
              Kolektor — sesja czasowa ({sessionHours}h)
            </div>
          )}

          {/* Banner QR — telefon po zeskanowaniu (loading) */}
          {qrTokenParam && qrValid === null && (
            <div
              style={{
                background: '#d1ecf1',
                color: '#0c5460',
                padding: '12px 15px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px',
                border: '1px solid #bee5eb',
                textAlign: 'center'
              }}
            >
              Weryfikacja kodu QR...
            </div>
          )}

          {error && (
            <div
              style={{
                background: 'var(--danger-light)',
                color: 'var(--danger)',
                padding: '12px 15px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px',
                border: '1px solid var(--danger-light)'
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label
                htmlFor="login"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: 'var(--text)',
                  fontSize: '14px'
                }}
              >
                Login
              </label>
              <input
                type="text"
                id="login"
                name="login"
                value={credentials.login}
                onChange={handleInputChange}
                autoComplete="username"
                placeholder="Wprowadź login"
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: fieldErrors.login ? '2px solid var(--danger)' : '2px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '15px',
                  transition: 'border-color 0.2s',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = fieldErrors.login ? 'var(--danger)' : 'var(--success)'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.login ? 'var(--danger)' : 'var(--border)'}
              />
              {fieldErrors.login && (
                <div style={{ color: 'var(--danger)', fontSize: '13px', marginTop: '4px' }}>{fieldErrors.login}</div>
              )}
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label
                htmlFor="password"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: 'var(--text)',
                  fontSize: '14px'
                }}
              >
                Hasło
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                autoComplete="current-password"
                placeholder="Wprowadź hasło"
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: fieldErrors.password ? '2px solid var(--danger)' : '2px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '15px',
                  transition: 'border-color 0.2s',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = fieldErrors.password ? 'var(--danger)' : 'var(--success)'}
                onBlur={(e) => e.target.style.borderColor = fieldErrors.password ? 'var(--danger)' : 'var(--border)'}
              />
              {fieldErrors.password && (
                <div style={{ color: 'var(--danger)', fontSize: '13px', marginTop: '4px' }}>{fieldErrors.password}</div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading ? 'var(--text-muted)' : 'linear-gradient(135deg, var(--success), var(--success))',
                color: '#fff' ,
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 4px 15px rgba(25, 135, 84, 0.3)'
              }}
              onMouseOver={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              {loading ? 'Logowanie...' : 'Zaloguj się'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button
                type="button"
                onClick={() => setShowResetModal(true)}
                style={{
                  background: 'none', border: 'none', color: 'var(--info)',
                  fontSize: '13px', cursor: 'pointer', textDecoration: 'underline',
                  padding: '4px 8px',
                }}
              >
                Zmień hasło
              </button>
            </div>
          </form>
        </div>
      </div>

      <ResetPasswordModal isOpen={showResetModal} onClose={() => setShowResetModal(false)} />
    </div>
  );
};

export default LoginPage;
